import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useLocalFolderCheck from '../hooks/useLocalFolderCheck';

/**
 * Runs the local folder check in the background on this computer (see
 * utils/localFolderScan.js). Invisible while it can work on its own. The only
 * time it shows anything is when a folder was chosen on this browser but the
 * browser has since forgotten the permission (Chrome asks again after a
 * restart unless "Allow on every visit" was picked) — re-granting needs a
 * click, so a small prompt offers one. Dismissible for the session.
 */
export default function LocalFolderCheck({ companyId, savedPath }) {
  const { supported, handle, permission, allow } = useLocalFolderCheck(companyId, {
    auto: true,
    savedPath
  });
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('bhFileSyncPromptDismissed') === '1';
    } catch {
      return false;
    }
  });

  if (!supported || !handle || permission === 'granted' || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('bhFileSyncPromptDismissed', '1');
    } catch {
      /* session-only convenience */
    }
  };

  return (
    <Box
      data-testid="local-folder-check-prompt"
      sx={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: 1300,
        maxWidth: 340,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: 'background.paper',
        boxShadow: 4,
        border: '1px solid rgba(96,165,250,0.4)'
      }}
    >
      <Typography sx={{ fontSize: '13px', mb: 1 }}>
        Allow Beiing Human to check your invoice folder <strong>{savedPath}</strong> on this
        computer, so missing invoice images are caught before Foundation shows "File not Found".
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={dismiss}>
          Not now
        </Button>
        <Button size="small" variant="contained" onClick={allow}>
          Allow
        </Button>
      </Box>
    </Box>
  );
}
